import { Link, useLocation } from 'react-router-dom';
import * as api from '../../api/client';
import { useAvailabilityReport } from '../../hooks/useReports';
import { useReportParams } from './ScopeRangePicker';
import { Card } from '../common/Card';
import { Skeleton } from '../common/Skeleton';
import { Stat } from '../common/Stat';
import { formatBudgetRemaining } from '../../utils/series';
import { GradeBadge } from './GradeBadge';
import { DeltaValue } from './DeltaValue';
import { CoverageNote } from './CoverageNote';
import { ServiceReportRow } from '../../types';

/** Every metric here is nullable. Null renders as an em dash, never as 0 or 100. */
const num = (v: number | null | undefined, digits = 0, suffix = '') =>
  v === null || v === undefined ? '—' : `${v.toFixed(digits)}${suffix}`;

/** Error budget bar. Fills to 100% max; turns red once the service has gone over budget. */
function BudgetBar({ consumed }: { consumed: number | null }) {
  if (consumed === null) {
    return <span className="text-gray-400">—</span>;
  }
  const over = consumed > 100;
  const width = Math.min(consumed, 100);
  return (
    <div className="flex items-center gap-2 justify-end">
      <span className="font-mono tabular-nums text-xs w-16 text-right">{consumed.toFixed(2)}%</span>
      <div className="budget-bar-track w-20 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden shrink-0">
        <div
          className={`budget-bar-fill h-full rounded-full ${over ? 'bg-status-down' : 'bg-status-up'}`}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

export function AvailabilityReport() {
  const { query } = useReportParams();
  const { data, isLoading, isError } = useAvailabilityReport(query);
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Loading report">
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }
  if (isError || !data) {
    return <Card className="p-10 text-center text-gray-500 dark:text-gray-400">Could not load this report.</Card>;
  }
  if (data.rows.length === 0) {
    return <Card className="p-10 text-center text-gray-500 dark:text-gray-400">No services match this scope.</Card>;
  }

  const overBudget = data.rows.filter(r => r.error_budget_consumed !== null && r.error_budget_consumed > 100);
  const withinBudget = data.rows.filter(r => r.error_budget_consumed !== null && r.error_budget_consumed <= 100);
  const worst = data.rows.reduce<ServiceReportRow | null>((acc, r) => {
    if (r.error_budget_consumed === null) return acc;
    if (acc === null || acc.error_budget_consumed === null) return r;
    return r.error_budget_consumed > acc.error_budget_consumed ? r : acc;
  }, null);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat
          label="Over budget"
          value={String(overBudget.length)}
          note="services past 100% of their SLA budget"
          alarm={overBudget.length > 0}
        />
        <Stat
          label="Within budget"
          value={String(withinBudget.length)}
          note="services still inside their SLA budget"
        />
        <Stat
          label="Worst offender"
          value={worst ? worst.service_name : '—'}
          note={worst ? `${num(worst.error_budget_consumed, 1, '%')} of budget consumed` : 'No data in this period'}
          alarm={worst !== null && worst.error_budget_consumed !== null && worst.error_budget_consumed > 100}
        />
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <CoverageNote coverage={data.meta.coverage} />
        <a
          href={api.reportCsvUrl('availability', query)}
          download
          className="no-print text-sm text-brand-600 dark:text-brand-400 hover:underline"
        >
          Export CSV
        </a>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th scope="col" className="text-left px-4 py-3">Service</th>
              <th scope="col" className="text-right px-2 py-3">Target</th>
              <th scope="col" className="text-right px-2 py-3">Uptime</th>
              <th scope="col" className="text-right px-2 py-3">Error budget consumed</th>
              <th scope="col" className="text-right px-2 py-3">Budget remaining</th>
              <th scope="col" className="text-left px-4 py-3">Grade</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {data.rows.map(r => (
              <tr key={r.service_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                <td className="px-4 py-2.5">
                  <Link
                    to={{ pathname: `/reports/services/${r.service_id}`, search: location.search }}
                    className="text-brand-600 dark:text-brand-400 hover:underline"
                  >
                    {r.service_name}
                  </Link>
                </td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">
                  {r.sla_target}%
                  {r.sla_target_inherited && (
                    <span
                      className="ml-1 align-middle text-[10px] uppercase tracking-wide text-gray-400"
                      title="No SLA target set on this service — using the default"
                    >
                      inherited
                    </span>
                  )}
                </td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">
                  {num(r.uptime, 3, '%')}
                  {r.uptime_delta !== undefined && (
                    <div><DeltaValue value={r.uptime_delta} unit="pp" /></div>
                  )}
                </td>
                <td className="px-2 py-2.5">
                  <BudgetBar consumed={r.error_budget_consumed} />
                </td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">
                  {formatBudgetRemaining(
                    r.error_budget_remaining_minutes === null ? null : r.error_budget_remaining_minutes * 60
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <GradeBadge grade={r.grade} budgetConsumed={r.error_budget_consumed} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
